import {useDestinationImage} from "../../hooks/useDestinationImage";
import TravelRequirementsBadge from "../travel/TravelRequirementsBadge";
import React from "react";
import { RoundTripFlightMatch } from "../../models/RoundTripFlightMatch";
import RoundTripFlightInfoBlock from "./RoundTripFlightInfoBlock";

interface RoundTripCardProps {
  roundTripFlightMatch: RoundTripFlightMatch;
  imageUrl: string;
}

const RoundTripListItem: React.FC<RoundTripCardProps> = ({
  roundTripFlightMatch,
  imageUrl,
}) => {
  const { round_trip_flight } = roundTripFlightMatch;
  const outbound = round_trip_flight.outbound_flight;
  const hasConnection =
    (round_trip_flight.outbound_flights?.length ?? 0) > 1 ||
    (round_trip_flight.inbound_flights?.length ?? 0) > 1;

  const legs = round_trip_flight.outbound_flights;
  const destinationIata = legs?.length ? legs[legs.length - 1].destination_iata : outbound.destination_iata;
  const {src: imageSrc} = useDestinationImage(hasConnection ? "" : destinationIata, "list_short", imageUrl);

  // Prepared list PNGs already contain transparent padding and rounded corners.
  const isPreparedListImage = imageSrc.startsWith("/images/destinations/list_short/");

  return (
    <div className={`flight-result-card flex flex-col items-stretch justify-between rounded-2xl shadow-card bg-white gap-3 md:gap-6 xl:gap-10 w-full min-w-0 transition-smooth hover:scale-[1.01] will-change-transform ${hasConnection ? "xl:flex-row xl:items-center min-h-[142px] p-4 md:px-6 md:py-5" : "lg:flex-row lg:items-center p-4 md:py-4 xl:py-0 xl:pl-1.5 xl:pr-4"}`}>
      {/* Destination image (only shown on lg and up) */}
      {hasConnection ? (
        <div className="hidden xl:flex flex-col justify-center items-start text-start min-w-[180px] max-w-[180px]">
          <span title={outbound.destination_airport} className="w-full truncate text-xl font-extrabold tracking-tight text-[#2D2D2D] leading-tight">
            {outbound.destination_airport}
          </span>
          <span className="mt-1 text-xs font-bold uppercase tracking-wide text-[#B08EFE]">
            Ryanair
          </span>
        </div>
      ) : (
        <div className="hidden xl:flex items-center rounded-xl min-w-[180px] max-w-[180px] h-[80px] relative transition-smooth">
          <div className={isPreparedListImage
            ? "absolute inset-0"
            : "absolute inset-x-[2px] inset-y-[11px] overflow-hidden rounded-xl"}>
            <img
              src={imageSrc}
              alt={outbound.destination_airport}
              className="block w-full h-full object-cover"
            />
          </div>
          <span title={outbound.destination_airport} className="absolute left-4 right-3 bottom-6 truncate text-white text-base lg:text-lg font-bold">
            {outbound.destination_airport}
          </span>
        </div>
      )}

      {/* Compact destination text (shown on smaller screens) */}
      <div className={`flex flex-col text-start min-w-0 ${hasConnection ? "xl:hidden" : "xl:hidden lg:min-w-[100px] lg:max-w-[140px]"}`}>
          <span title={outbound.destination_airport} className="truncate text-base sm:text-lg font-bold tracking-tight text-[#2D2D2D]">
            {outbound.destination_airport}
          </span>
          <span className="text-xs sm:text-sm font-semibold tracking-tight text-[#B08EFE]">Ryanair</span>
      </div>

      <TravelRequirementsBadge requirements={roundTripFlightMatch.travel_requirements} />
      {/* Embedded flight info block */}
      <RoundTripFlightInfoBlock roundTripFlight={round_trip_flight} showShare />
    </div>
  );
};

export default RoundTripListItem;
